/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace frechet
*/
var frechet = {};

/**
* @name cdf
* @memberof frechet
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/frechet/cdf}
*/
import cdf from './../../../../base/dists/frechet/cdf';
setReadOnly( frechet, 'cdf', cdf );

/**
* @name Frechet
* @memberof frechet
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/frechet/ctor}
*/
import Frechet from './../../../../base/dists/frechet/ctor';
setReadOnly( frechet, 'Frechet', Frechet );

/**
* @name entropy
* @memberof frechet
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/frechet/entropy}
*/
import entropy from './../../../../base/dists/frechet/entropy';
setReadOnly( frechet, 'entropy', entropy );

/**
* @name kurtosis
* @memberof frechet
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/frechet/kurtosis}
*/
import kurtosis from './../../../../base/dists/frechet/kurtosis';
setReadOnly( frechet, 'kurtosis', kurtosis );

/**
* @name logcdf
* @memberof frechet
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/frechet/logcdf}
*/
import logcdf from './../../../../base/dists/frechet/logcdf';
setReadOnly( frechet, 'logcdf', logcdf );

/**
* @name logpdf
* @memberof frechet
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/frechet/logpdf}
*/
import logpdf from './../../../../base/dists/frechet/logpdf';
setReadOnly( frechet, 'logpdf', logpdf );

/**
* @name mean
* @memberof frechet
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/frechet/mean}
*/
import mean from './../../../../base/dists/frechet/mean';
setReadOnly( frechet, 'mean', mean );

/**
* @name median
* @memberof frechet
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/frechet/median}
*/
import median from './../../../../base/dists/frechet/median';
setReadOnly( frechet, 'median', median );

/**
* @name mode
* @memberof frechet
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/frechet/mode}
*/
import mode from './../../../../base/dists/frechet/mode';
setReadOnly( frechet, 'mode', mode );

/**
* @name pdf
* @memberof frechet
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/frechet/pdf}
*/
import pdf from './../../../../base/dists/frechet/pdf';
setReadOnly( frechet, 'pdf', pdf );

/**
* @name quantile
* @memberof frechet
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/frechet/quantile}
*/
import quantile from './../../../../base/dists/frechet/quantile';
setReadOnly( frechet, 'quantile', quantile );

/**
* @name skewness
* @memberof frechet
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/frechet/skewness}
*/
import skewness from './../../../../base/dists/frechet/skewness';
setReadOnly( frechet, 'skewness', skewness );

/**
* @name stdev
* @memberof frechet
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/frechet/stdev}
*/
import stdev from './../../../../base/dists/frechet/stdev';
setReadOnly( frechet, 'stdev', stdev );

/**
* @name variance
* @memberof frechet
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/frechet/variance}
*/
import variance from './../../../../base/dists/frechet/variance';
setReadOnly( frechet, 'variance', variance );


// EXPORTS //

export default frechet;
